/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import { Box, useTheme, useMediaQuery, Switch, FormControlLabel, Chip } from "@mui/material"
import { collection, onSnapshot, doc, updateDoc } from "firebase/firestore"
import { toast } from 'react-toastify'
import { db } from "../../firebase"
import { tokens } from "../../theme"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { useAuthState } from "../../components/hooks/useAuthState"
import useDocumentSubscription from "../../components/hooks/useDocumentSubscription"
import { User } from "../../models/user"

const Users = () => {
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [items, setItems] = useState<Array<any>>([])
  const [processing, setProcessing] = useState<boolean>(false)
  const { userId } = useAuthState()
  const currentUser = useDocumentSubscription<User>(
    userId ? doc(db, "users", userId) : null,
    User,
    [userId]
  )
  const usersRef = collection(db, 'users')

  useEffect(() => {
    const unsubscribe = onSnapshot(usersRef, ({ docs }) => {
      setItems(docs.map((docSnap, i) => {
        return ({
          No: i + 1,
          ...docSnap.data(),
          id: docSnap.id
        })
      }))
    })
    return unsubscribe
  }, [])

  const toggleAdmin = async (userId: string, currentIsAdmin: boolean) => {
    if (!currentUser?.isAdmin) {
      toast.error("管理者権限がありません")
      return
    }

    if (userId === currentUser.id) {
      toast.error("自分自身の管理者権限は変更できません")
      return
    }

    const newStatus = !currentIsAdmin
    const confirmMessage = newStatus
      ? "このユーザーに管理者権限を付与しますか？"
      : "このユーザーの管理者権限を解除しますか？"

    if (!window.confirm(confirmMessage)) return

    setProcessing(true)
    try {
      await updateDoc(doc(db, "users", userId), {
        isAdmin: newStatus
      })
      toast.success(newStatus ? "管理者権限を付与しました" : "管理者権限を解除しました")
    } catch (e) {
      toast.error("権限の変更に失敗しました: " + JSON.stringify(e))
    } finally {
      setProcessing(false)
    }
  }

  const columns: GridColDef[] = isMobile ? [
    {
      field: "No",
      headerName: "No",
      width: 50,
    },
    {
      field: "email",
      headerName: "メールアドレス",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "isAdmin",
      headerName: "管理者",
      width: 80,
      renderCell: (params: any) => (
        params.row.isAdmin ? (
          <Chip label="管理者" color="primary" size="small" />
        ) : (
          <Chip label="一般" size="small" />
        )
      ),
    },
  ] : [
    {
      field: "No",
      headerName: "No",
      width: 70,
    },
    {
      field: "email",
      headerName: "メールアドレス",
      flex: 2,
      cellClassName: "name-column--cell",
    },
    {
      field: "isAdmin",
      headerName: "管理者権限",
      flex: 1,
      renderCell: (params: any) => (
        params.row.isAdmin ? (
          <Chip label="管理者" color="primary" />
        ) : (
          <Chip label="一般ユーザー" />
        )
      ),
    },
    {
      field: "actions",
      headerName: "操作",
      flex: 1,
      renderCell: (params: any) => (
        <FormControlLabel
          control={
            <Switch
              checked={params.row.isAdmin}
              onChange={() => toggleAdmin(params.row.id, params.row.isAdmin)}
              disabled={processing || params.row.id === currentUser?.id}
              color="primary"
            />
          }
          label={params.row.isAdmin ? "管理者" : "一般"}
        />
      ),
    },
  ]

  return (
    <Box m={isMobile ? 2 : 5}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <h2>ユーザー管理</h2>
          <p style={{ color: colors.grey[300], fontSize: "0.9em" }}>
            登録されているユーザーの管理者権限を設定・解除できます
          </p>
        </Box>
      </Box>

      <Box
        m={isMobile ? 1 : 2}
        height={isMobile ? "70vh" : "75vh"}
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        <DataGrid
          rows={items}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
          }}
          pageSizeOptions={[25, 50, 100]}
        />
      </Box>
    </Box>
  )
}

export default Users
